import { forwardRef, useId, memo } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';

import { Message } from '../Message';

import type { RadioProps, FontSizeValue } from './Radio.types';
import type { Theme } from '../../theme';

/**
 * Map theme font size token to actual value
 */
const getFontSizeValue = (fontSize: FontSizeValue | undefined, theme: Theme): string | undefined => {
  if (!fontSize) {
    return undefined;
  }
  
  // Check if it's a theme token key
  const themeTokens = ['xs', 'sm', 'base', 'md', 'lg', 'xl'] as const;
  if (themeTokens.includes(fontSize as typeof themeTokens[number])) {
    return theme.typography.fontSize[fontSize as keyof typeof theme.typography.fontSize];
  }
  
  // Otherwise, treat as CSS value
  return typeof fontSize === 'number' ? `${fontSize}px` : String(fontSize);
};

/**
 * Container for the radio button and label
 */
const RadioContainer = styled.div<{
  $disabled?: boolean;
  $labelPosition: 'left' | 'right';
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
  width: fit-content;
  max-width: 100%;
  outline: none;
  border-radius: 4px;
  position: relative;
  padding: 2px 0;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  flex-direction: ${({ $labelPosition }) =>
    $labelPosition === 'left' ? 'row-reverse' : 'row'};
`;

/**
 * Hidden input for accessibility
 */
const HiddenRadio = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

/**
 * Outer ring of the radio button
 */
const OuterRing = styled.span<{
  theme?: Theme;
  $checked: boolean;
  $disabled: boolean;
  $error: boolean;
  $color?: string;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  min-width: 20px;
  height: 20px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  margin: 2px;
  border-radius: 50%;
  border: 1px solid
    ${({ theme, $error, $checked, $color }) =>
      $error
        ? theme.colors.semantic.red.base
        : $checked
        ? $color || theme.colors.highlight
        : theme.colors.grey[400]};
  background: transparent;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  flex-shrink: 0;
  position: relative;

  /* Hover effect */
  ${({ $disabled, theme }) =>
    !$disabled &&
    `
    &:hover {
      box-shadow: 0 0 0 2px ${theme.colors.transparent.black20};
    }
  `}

  /* Disabled styling */
  ${({ $disabled, theme }) =>
    $disabled &&
    `
    border-color: ${theme.colors.grey[300]};
    opacity: 0.6;
  `}
`;

/**
 * Inner dot of the radio button (visible when checked)
 */
const InnerDot = styled.span<{
  theme?: Theme;
  $checked: boolean;
  $disabled: boolean;
  $error: boolean;
  $color?: string;
}>`
  opacity: ${({ $checked }) => ($checked ? 1 : 0)};
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ theme, $error, $disabled, $color }) =>
    $error
      ? theme.colors.semantic.red.base
      : $disabled
      ? theme.colors.grey[400]
      : $color || theme.colors.highlight};
  transition: opacity 0.2s ease-in-out;
`;

/**
 * Label text for the radio
 */
const RadioLabel = styled.label<{
  theme?: Theme;
  $disabled?: boolean;
  $labelPosition: 'left' | 'right';
  $fontSize?: string;
}>`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme, $fontSize }) => $fontSize || theme.typography.fontSize.sm};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.grey[400] : theme.colors.grey[700]};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  display: inline-block;
  min-height: 24px;
  line-height: 24px;
  user-select: none;
`;

/**
 * Wrapper for the entire radio component
 */
const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * Container for messages
 */
const MessageContainer = styled.div`
  margin-left: 30px;
  margin-top: 2px;
`;

/**
 * Radio - A single radio button component
 *
 * Used for selecting one option from a group. Usually used within a RadioGroup,
 * but can also be used standalone for simple boolean choices.
 *
 * @example
 * ```tsx
 * <Radio
 *   value="option1"
 *   label="Option 1"
 *   checked={selected === 'option1'}
 *   onChange={(e, checked) => setSelected('option1')}
 * />
 * ```
 */
const RadioComponent = forwardRef<HTMLDivElement, RadioProps>(
  (
    {
      value,
      label,
      labelPosition = 'right',
      checked = false,
      disabled = false,
      error = false,
      color,
      name,
      id: providedId,
      'aria-label': ariaLabel,
      onChange,
      tabIndex,
      errorMessage,
      assistiveMessage,
      className,
      'data-testid': testId,
      labelFontSize,
    },
    ref
  ) => {
    const generatedId = useId();
    const radioId = providedId || generatedId;
    const labelId = `${radioId}-label`;
    const hasVisibleLabel = Boolean(label);
    const theme = useTheme() as Theme;
    
    // Resolve font size - if it's already a resolved string (from RadioGroup), use it directly
    const resolvedFontSize = typeof labelFontSize === 'string' && !['xs', 'sm', 'base', 'md', 'lg', 'xl'].includes(labelFontSize)
      ? labelFontSize
      : getFontSizeValue(labelFontSize, theme);

    const actualTabIndex = tabIndex !== undefined ? tabIndex : disabled ? -1 : 0;

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;

      event.preventDefault();
      event.stopPropagation();
      onChange?.(event, !checked);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        onChange?.(event, !checked);
      }
    };

    return (
      <RadioWrapper className={className} data-testid={testId}>
        <RadioContainer
          ref={ref}
          role="radio"
          aria-checked={checked}
          aria-disabled={disabled}
          aria-labelledby={hasVisibleLabel ? labelId : undefined}
          aria-label={!hasVisibleLabel ? ariaLabel || 'Radio option' : undefined}
          tabIndex={actualTabIndex}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          $disabled={disabled}
          $labelPosition={labelPosition}
          data-testid={testId && `${testId}-container`}
        >
          <HiddenRadio
            type="radio"
            id={radioId}
            name={name}
            value={String(value)}
            checked={checked}
            disabled={disabled}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
          />
          <OuterRing
            $checked={checked}
            $disabled={disabled}
            $error={error}
            $color={color}
            data-testid={testId && `${testId}-ring`}
          >
            <InnerDot
              $checked={checked}
              $disabled={disabled}
              $error={error}
              $color={color}
            />
          </OuterRing>
          {label && (
            <RadioLabel
              id={labelId}
              htmlFor={radioId}
              $disabled={disabled}
              $labelPosition={labelPosition}
              $fontSize={resolvedFontSize}
            >
              {label}
            </RadioLabel>
          )}
        </RadioContainer>
        {errorMessage && error && (
          <MessageContainer>
            <Message variant="error">{errorMessage}</Message>
          </MessageContainer>
        )}
        {assistiveMessage && !errorMessage && (
          <MessageContainer>
            <Message variant="assistive">{assistiveMessage}</Message>
          </MessageContainer>
        )}
      </RadioWrapper>
    );
  }
);

RadioComponent.displayName = 'Radio';

export const Radio = memo(RadioComponent);

export default Radio;
